import React, { useRef, useState } from 'react';

export const REPORT_FIELD_TYPES = [
  ['text', 'Short text'],
  ['textarea', 'Long text'],
  ['dropdown', 'Dropdown'],
  ['date', 'Date'],
  ['time', 'Exact time'],
  ['datetime', 'Date and exact time'],
  ['number', 'Number'],
  ['radio', 'Single choice'],
  ['yes_no', 'Yes / No'],
  ['checkbox', 'Confirmation checkbox'],
  ['airport', 'Airport code'],
  ['image', 'Image upload'],
];

export function fieldOptions(field) {
  if (!Array.isArray(field?.options)) return [];
  return field.options.map((option) => {
    if (typeof option === 'string') return { label: option, value: option };
    return { label: option.label || option.value || '', value: option.value ?? option.label ?? '' };
  }).filter((option) => option.label);
}

function InputShell({ field, inputId, children, error }) {
  return (
    <div className={`intelex-field intelex-field-${field.type || 'text'} intelex-width-${Number(field.width) || 12}${error ? ' has-error' : ''}`}>
      {field.type !== 'checkbox' && <label className="intelex-field-label" htmlFor={inputId}>{field.label || 'Untitled field'}{field.required && <span aria-hidden="true"> *</span>}</label>}
      {field.help && <p className="intelex-field-help" id={`${inputId}-help`}>{field.help}</p>}
      {children}
      {error && <p className="intelex-field-error" id={`${inputId}-error`} role="alert">{error}</p>}
    </div>
  );
}

function ImageUploadField({ field, inputId, value, onChange, onUploadImage, onRemoveImage, error, disabled }) {
  const inputRef = useRef(null);
  const [state, setState] = useState({ uploading: false, error: '' });
  const items = Array.isArray(value) ? value : [];
  const limit = Math.min(5, Math.max(1, Number(field.maxFiles) || 1));

  const upload = async (event) => {
    const files = [...(event.target.files || [])].slice(0, Math.max(0, limit - items.length));
    event.target.value = '';
    if (!files.length || !onUploadImage) return;
    setState({ uploading: true, error: '' });
    const uploaded = [];
    try {
      for (const file of files) uploaded.push(await onUploadImage(file));
      onChange([...items, ...uploaded]);
      setState({ uploading: false, error: '' });
    } catch (uploadError) {
      onChange([...items, ...uploaded]);
      setState({ uploading: false, error: uploadError.message || 'The image could not be uploaded.' });
    }
  };

  const remove = async (item) => {
    setState((current) => ({ ...current, error: '' }));
    try {
      if (onRemoveImage) await onRemoveImage(item);
      onChange(items.filter((current) => String(current.id) !== String(item.id)));
    } catch (removeError) {
      setState((current) => ({ ...current, error: removeError.message || 'The image could not be removed.' }));
    }
  };

  return <InputShell field={field} inputId={inputId} error={error}>
    <div className="intelex-image-upload">
      {items.length > 0 && <div className="intelex-image-upload-grid">{items.map((item) => <figure key={item.id}>
        <img src={item.url || `/api/portal/report-files/${item.id}`} alt={item.name || 'Uploaded evidence'} />
        <figcaption><span>{item.name || 'Image'}</span><button type="button" onClick={() => remove(item)}>Remove</button></figcaption>
      </figure>)}</div>}
      {items.length < limit && <button className="intelex-image-picker" type="button" onClick={() => inputRef.current?.click()} disabled={disabled || state.uploading || !onUploadImage}>
        {state.uploading ? 'Uploading image…' : items.length ? 'Add another image' : 'Choose image'}
      </button>}
      <input ref={inputRef} id={inputId} className="sr-only" type="file" accept="image/jpeg,image/png" multiple={limit > 1} onChange={upload} />
      <small>JPEG or PNG, up to 5 MB each. {items.length} of {limit} uploaded.</small>
      {state.error && <p className="intelex-field-error" role="alert">{state.error}</p>}
    </div>
  </InputShell>;
}

export default function ReportField({ field, value, onChange, error = '', onUploadImage, onRemoveImage, disabled = false }) {
  const inputId = `report-field-${String(field.id || field.key || field.label).replace(/[^a-z0-9_-]/gi, '-')}`;
  const describedBy = [field.help ? `${inputId}-help` : '', error ? `${inputId}-error` : ''].filter(Boolean).join(' ') || undefined;
  const common = {
    id: inputId,
    name: String(field.id || field.key || field.label),
    required: Boolean(field.required),
    'aria-describedby': describedBy,
    'aria-invalid': Boolean(error),
    disabled,
  };
  const options = fieldOptions(field);

  if (field.type === 'image') {
    return <ImageUploadField field={field} inputId={inputId} value={value} onChange={onChange} onUploadImage={onUploadImage} onRemoveImage={onRemoveImage} error={error} disabled={disabled} />;
  }

  if (field.type === 'textarea') {
    return <InputShell field={field} inputId={inputId} error={error}><textarea {...common} rows={field.rows || 6} placeholder={field.placeholder || ''} value={value ?? ''} onChange={(event) => onChange(event.target.value)} /></InputShell>;
  }
  if (field.type === 'select' || field.type === 'dropdown') {
    return <InputShell field={field} inputId={inputId} error={error}><select {...common} value={value ?? ''} onChange={(event) => onChange(event.target.value)}><option value="">Select an option</option>{options.map((option, index) => <option key={`${option.value}-${index}`} value={option.value}>{option.label}</option>)}</select></InputShell>;
  }
  if (field.type === 'radio' || field.type === 'yes_no') {
    const choices = field.type === 'yes_no' ? [{ label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' }] : options;
    return <InputShell field={field} inputId={inputId} error={error}><fieldset className="intelex-choice-group" aria-describedby={describedBy} disabled={disabled}><legend className="sr-only">{field.label}</legend>{choices.map((option, index) => <label key={`${option.value}-${index}`}><input type="radio" name={common.name} value={option.value} required={field.required && index === 0} checked={String(value ?? '') === String(option.value)} onChange={() => onChange(option.value)} /><span>{option.label}</span></label>)}</fieldset></InputShell>;
  }
  if (field.type === 'checkbox') {
    return <InputShell field={field} inputId={inputId} error={error}><label className="intelex-checkbox" htmlFor={inputId}><input {...common} type="checkbox" checked={Boolean(value)} onChange={(event) => onChange(event.target.checked)} /><span>{field.label || 'Confirmation'}{field.required && <b aria-hidden="true"> *</b>}</span></label></InputShell>;
  }

  const htmlType = { date: 'date', time: 'time', datetime: 'datetime-local', number: 'number' }[field.type] || 'text';
  const airport = field.type === 'airport';
  return <InputShell field={field} inputId={inputId} error={error}><input {...common} type={htmlType} min={field.type === 'number' && field.min !== null ? field.min : undefined} max={field.type === 'number' && field.max !== null ? field.max : undefined} step={field.type === 'time' || field.type === 'datetime' ? '60' : field.type === 'number' ? (field.step || 'any') : undefined} inputMode={field.type === 'number' ? 'decimal' : undefined} maxLength={airport ? 4 : undefined} autoCapitalize={airport ? 'characters' : undefined} placeholder={field.placeholder || (airport ? 'ICAO' : '')} value={value ?? ''} onChange={(event) => onChange(airport ? event.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 4) : event.target.value)} /></InputShell>;
}
